import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Wallet, CreditCard, Landmark, Coins, FileText } from "lucide-react";
import { toast } from "sonner";

const TYPE_ICON = { cash: Coins, bank: Landmark, card: CreditCard, wallet: Wallet };
const TYPE_LABEL = { cash: "Cash", bank: "Bank", card: "Card", wallet: "Digital wallet" };

export default function Accounts() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ name: "", type: "cash", starting_balance: "0", currency, statement_balance: "", statement_date: "" });
  // iter 72l — inline "update statement balance" dialog per account row
  const [stmtEditing, setStmtEditing] = useState(null); // account object being edited
  const [stmtForm, setStmtForm] = useState({ statement_balance: "", statement_date: "" });

  const load = async () => {
    const { data } = await api.get("/accounts");
    setAccounts(data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/accounts", {
        name: form.name,
        type: form.type,
        starting_balance: Number(form.starting_balance || 0),
        currency: form.currency,
        statement_balance: form.statement_balance !== "" ? Number(form.statement_balance) : null,
        statement_date: form.statement_date || null,
      });
      toast.success("Account added");
      setOpen(false);
      setForm({ name: "", type: "cash", starting_balance: "0", currency, statement_balance: "", statement_date: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add account");
    }
  };

  // iter 72l — save just the statement balance/date for reconciliation
  const saveStatement = async () => {
    if (!stmtEditing) return;
    if (stmtForm.statement_balance === "") return toast.error("Enter the balance from your statement");
    try {
      await api.patch(`/accounts/${stmtEditing.id}`, {
        statement_balance: Number(stmtForm.statement_balance),
        statement_date: stmtForm.statement_date || null,
      });
      toast.success("Statement balance updated");
      setStmtEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    await api.delete(`/accounts/${deleteId}`);
    toast.success("Account removed");
    setDeleteId(null);
    load();
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Wallets</div>
          <h1 className="font-display text-4xl mt-2">Accounts</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1">Track money across cash, bank, card and digital wallets.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-account-btn" className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-11 px-5">
              <Plus className="w-4 h-4 mr-2" /> New account
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Create account</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  data-testid="account-name" required
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. GTB Savings"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="account-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TYPE_LABEL).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`account-type-${k}`}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger data-testid="account-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="NGN">NGN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Starting balance</Label>
                <Input
                  data-testid="account-balance"
                  type="number" step="0.01"
                  value={form.starting_balance}
                  onChange={(e) => setForm({ ...form, starting_balance: e.target.value })}
                />
              </div>
              {form.type === "bank" && (
                <div className="rounded-lg border border-dashed border-[var(--divider)] p-3 space-y-3">
                  <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)] font-semibold">
                    Statement balance <span className="normal-case tracking-normal text-[10px] text-[var(--muted-text)] font-normal">· optional, used for Reconciliation report</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">From bank statement</Label>
                      <Input
                        data-testid="account-statement-balance"
                        type="number" step="0.01"
                        value={form.statement_balance}
                        onChange={(e) => setForm({ ...form, statement_balance: e.target.value })}
                        placeholder="e.g. 1250.42" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Statement date</Label>
                      <Input
                        type="date"
                        value={form.statement_date}
                        onChange={(e) => setForm({ ...form, statement_date: e.target.value })} />
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  data-testid="account-submit"
                  type="submit"
                  disabled={!form.name}
                  className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
                >
                  Add account
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {accounts.length === 0 ? (
        <div data-testid="accounts-empty" className="card-flat p-12 text-center">
          <Wallet className="w-8 h-8 mx-auto text-[var(--muted-text)]" />
          <h3 className="font-display text-xl mt-3">No accounts yet</h3>
          <p className="text-sm text-[var(--muted-text)] mt-1">Add wallets to organise where your money lives.</p>
        </div>
      ) : (
        <div data-testid="accounts-list" className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {accounts.map((a) => {
            const Icon = TYPE_ICON[a.type] || Coins;
            return (
              <div key={a.id} data-testid={`account-row-${a.id}`} className="card-flat p-6 hover-lift">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-md bg-[#2F4F4F] text-[#FDFCF8] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <Button
                    data-testid={`account-delete-${a.id}`}
                    size="icon" variant="ghost"
                    onClick={() => setDeleteId(a.id)}
                  >
                    <Trash2 className="w-4 h-4 text-[#E63946]" />
                  </Button>
                </div>
                <div className="mt-4">
                  <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">{TYPE_LABEL[a.type]}</div>
                  <h3 className="font-display text-xl mt-1">{a.name}</h3>
                </div>
                <div className="mt-3 font-mono-data text-2xl font-semibold">
                  {formatCurrency(a.balance != null ? a.balance : a.starting_balance, a.currency)}
                </div>
                <div className="mt-1 text-xs text-[var(--muted-text)]">
                  {a.balance != null ? "Current balance" : "Starting balance"} · {a.currency}
                </div>
                {/* iter 72l — statement balance surface + updater */}
                {a.type === "bank" && (
                  <div className="mt-3 pt-3 border-t border-[var(--divider)]">
                    {a.statement_balance != null ? (
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">
                          Statement · {a.statement_date ? formatDate(a.statement_date) : "no date"}
                        </div>
                        <div className="font-mono-data text-sm">
                          {formatCurrency(a.statement_balance, a.currency)}
                        </div>
                      </div>
                    ) : (
                      <div className="text-[10px] tracking-widest uppercase text-[var(--muted-text)]">
                        No statement on file
                      </div>
                    )}
                    <Button
                      size="sm" variant="ghost"
                      data-testid={`account-update-statement-${a.id}`}
                      className="mt-2 h-7 text-xs w-full justify-start px-2 text-[var(--brand)] hover:bg-[var(--brand-light)]"
                      onClick={() => {
                        setStmtEditing(a);
                        setStmtForm({
                          statement_balance: a.statement_balance != null ? String(a.statement_balance) : "",
                          statement_date: a.statement_date || "",
                        });
                      }}
                    >
                      <FileText className="w-3.5 h-3.5 mr-1.5" /> Update statement balance
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* iter 72l — Statement balance update dialog */}
      <Dialog open={!!stmtEditing} onOpenChange={(v) => !v && setStmtEditing(null)}>
        <DialogContent className="max-w-md" data-testid="account-statement-dialog">
          <DialogHeader>
            <DialogTitle>Update statement balance</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[var(--muted-text)]">
              Enter the balance shown on your latest {stmtEditing?.name} statement. The Personal Reports · Reconciliation section will show any difference vs the app's computed balance.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Statement balance ({stmtEditing?.currency})</Label>
                <Input
                  data-testid="account-statement-balance-edit"
                  type="number" step="0.01"
                  value={stmtForm.statement_balance}
                  onChange={(e) => setStmtForm({ ...stmtForm, statement_balance: e.target.value })}
                  autoFocus />
              </div>
              <div>
                <Label>Statement date</Label>
                <Input
                  type="date"
                  value={stmtForm.statement_date}
                  onChange={(e) => setStmtForm({ ...stmtForm, statement_date: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStmtEditing(null)}>Cancel</Button>
            <Button
              data-testid="account-statement-save"
              onClick={saveStatement}
              className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete account?</AlertDialogTitle>
            <AlertDialogDescription>Transactions linked to this account will remain but lose their account tag.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="account-delete-confirm"
              onClick={remove}
              className="bg-[#E63946] hover:bg-[#c92e3b] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
